
import React from "react";
import { LoadingSpinner } from "./LoadingSpinner";
import { Card, CardContent } from "./card";
import { cn } from "@/lib/utils";

interface LoadingScreenProps {
  /** Custom message to display */
  message?: string;
  /** Secondary message or description */
  description?: string;
  /** Size variant of the spinner */
  size?: "sm" | "md" | "lg";
  /** Show a card background */
  withCard?: boolean;
  /** Fullscreen mode - takes up entire parent container */
  fullscreen?: boolean;
  /** Additional class names */
  className?: string;
  /** Position the loader in the center of the screen */
  centered?: boolean;
}

export function LoadingScreen({
  message = "Loading content...",
  description,
  size = "md",
  withCard = true,
  fullscreen = false,
  className,
  centered = false,
}: LoadingScreenProps) {
  const content = (
    <>
      <LoadingSpinner
        message={message}
        size={size}
        centered={true}
        pulse={true}
        variant="primary"
        className="mb-2"
      />
      {description && (
        <p className="text-xs text-muted-foreground mt-4 animate-fadeIn">
          {description}
        </p>
      )}
    </>
  );

  const containerClasses = cn(
    "flex justify-center items-center",
    fullscreen ? "min-h-screen w-full fixed inset-0 z-50 bg-background/95" : "min-h-[300px]",
    centered && !fullscreen && "fixed inset-0 z-50 bg-background/95",
    className
  );

  if (withCard) {
    return (
      <div className={containerClasses}>
        <Card className="w-full max-w-md shadow-md border-muted animate-scaleIn">
          <CardContent className="p-6 flex flex-col items-center text-center">
            {content}
          </CardContent>
        </Card>
      </div>
    );
  }

  return <div className={cn(containerClasses, "flex-col")}>{content}</div>;
}
